import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useFloatingUi } from '../FloatingUiManager';

// Front-end editing is disabled site-wide (Taiwo 2026-06-30): no "Edit" pill on
// public pages. Set to false to re-enable. All content edits go through the
// admin Studio instead. Auth bypass is also off so the pill can never leak.
const FRONTEND_EDIT_ENABLED = false;
const DEV_SKIP_AUTH = false;

const ADMIN_PAGES = new Set([
  'admin', 'admin-login', 'admin-oauth-callback',
  'vendor', 'vendor-login', 'vendor-signup', 'vendor-activate',
  'vendor-confirm-email', 'vendor-forgot-password', 'vendor-reset-password',
  'portal', 'getting-married', 'magazine-studio', 'magazine-article',
  'couple-signup', 'couple-login', 'couple-confirm-email',
  'couple-forgot-password', 'couple-reset-password',
  'event-review', 'join',
]);

const STATIC_SHOWCASE_SLUGS = {
  'dde-showcase':           'domaine-des-etangs',
  'ritz-showcase':          'the-ritz-london',
  'ic-park-lane-showcase':  'intercontinental-london-park-lane',
  'gt-showcase':            'grand-tirolia-kitzbuehel',
  'sskrabey-showcase':      'six-senses-krabey-island',
};

const CMS_PAGE_LABELS = {
  'privacy':        'Edit Privacy Policy',
  'terms':          'Edit Terms',
  'cookies':        'Edit Cookies',
  'reviews-policy': 'Edit Reviews Policy',
  'support':        'Edit Support',
};

function getEditContext(page, { showcaseSlug, venueSlug, magazineSlug, eventSlug, locationSlug, countrySlug, regionSlug }) {
  if (STATIC_SHOWCASE_SLUGS[page]) {
    return {
      type:     'showcase-static',
      label:    'Edit Showcase',
      slug:     STATIC_SHOWCASE_SLUGS[page],
      disabled: true,
    };
  }
  if (CMS_PAGE_LABELS[page]) {
    return { type: 'cms-page', label: CMS_PAGE_LABELS[page], pageKey: page, disabled: false };
  }
  switch (page) {
    case 'showcase':
      return { type: 'showcase', label: 'Edit Showcase', slug: showcaseSlug, disabled: false };
    case 'magazine-article':
      return { type: 'article', label: 'Edit Article', slug: magazineSlug, disabled: false };
    case 'event-detail':
      return { type: 'event', label: 'Edit Event', slug: eventSlug, disabled: false };
    case 'location':
      return { type: 'location', label: 'Edit Country', slug: locationSlug, disabled: false };
    case 'region':
      return { type: 'location', label: 'Edit Region', slug: regionSlug, countrySlug, disabled: false };
    default:
      return null;
  }
}

export default function GlobalAdminBar({ page, slugs = {}, onOpenAdmin }) {
  const { isAuthenticated } = useAdminAuth();
  const { compareBarOpen, compareBarHeight } = useFloatingUi();
  const [hovered,     setHovered]     = useState(false);
  const [tipVisible,  setTipVisible]  = useState(false);

  if (!FRONTEND_EDIT_ENABLED) return null;
  if (!isAuthenticated && !DEV_SKIP_AUTH) return null;
  if (ADMIN_PAGES.has(page)) return null;

  const ctx = getEditContext(page, slugs);
  if (!ctx) return null;

  // Shift up when compare bar is open so the pill doesn't overlap the Aura launcher
  const bottomOffset = 90 + (compareBarOpen ? compareBarHeight + 8 : 0);

  function handleClick() {
    const returnPath = window.location.pathname;

    if (ctx.disabled) {
      try {
        sessionStorage.setItem('lwd_admin_edit_intent', JSON.stringify({
          type:       'showcase-static',
          slug:       ctx.slug,
          returnPath,
        }));
      } catch {}
      onOpenAdmin?.();
      return;
    }

    try {
      sessionStorage.setItem('lwd_admin_edit_intent', JSON.stringify({
        type:        ctx.type,
        slug:        ctx.slug        || null,
        pageKey:     ctx.pageKey     || null,
        countrySlug: ctx.countrySlug || null,
        returnPath,
      }));
    } catch {}
    onOpenAdmin?.();
  }

  const pill = (
    <div
      style={{
        position:   'fixed',
        bottom:     bottomOffset,
        right:      28,
        zIndex:     9000,
        transition: 'bottom 0.3s cubic-bezier(0.25,0.46,0.45,0.94)',
        display:    'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap:        6,
        pointerEvents: 'none',
      }}
    >
      {ctx.disabled && tipVisible && (
        <div style={{
          pointerEvents:  'none',
          background:     'rgba(20,16,12,0.96)',
          color:          'rgba(245,240,232,0.75)',
          fontFamily:     'var(--font-body)',
          fontSize:       11,
          lineHeight:     1.5,
          padding:        '8px 12px',
          borderRadius:   6,
          maxWidth:       220,
          textAlign:      'right',
          border:         '1px solid rgba(211,154,33,0.18)',
        }}>
          Static template, not yet connected to Studio.
          Clicking opens the Showcase list so you can link a record.
        </div>
      )}

      <button
        onClick={handleClick}
        onMouseEnter={() => { setHovered(true); if (ctx.disabled) setTipVisible(true); }}
        onMouseLeave={() => { setHovered(false); setTipVisible(false); }}
        style={{
          pointerEvents:   'auto',
          display:         'flex',
          alignItems:      'center',
          gap:             6,
          padding:         '8px 14px',
          borderRadius:    100,
          border:          ctx.disabled
            ? '1px solid rgba(211,154,33,0.25)'
            : '1px solid rgba(211,154,33,0.4)',
          background:      hovered && !ctx.disabled
            ? 'rgba(40,32,20,0.97)'
            : 'rgba(20,16,12,0.93)',
          color:           ctx.disabled ? 'rgba(211,154,33,0.45)' : '#d39a21',
          fontFamily:      'var(--font-body)',
          fontSize:        11,
          fontWeight:      700,
          letterSpacing:   '0.08em',
          textTransform:   'uppercase',
          cursor:          ctx.disabled ? 'default' : 'pointer',
          opacity:         ctx.disabled ? 0.72 : 1,
          boxShadow:       '0 2px 12px rgba(0,0,0,0.35)',
          transition:      'background 0.18s, opacity 0.18s',
          userSelect:      'none',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        <span style={{ fontSize: 12, lineHeight: 1 }}>✦</span>
        {ctx.label}
      </button>
    </div>
  );

  return createPortal(pill, document.body);
}
